import { PostHogProvider, usePostHog } from '@posthog/react'
import posthog from 'posthog-js'
import { useEffect, useState, type ReactNode } from 'react'
import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  getBrowserAnalyticsConsent,
} from './analyticsConsent'
import type { PostHog } from 'posthog-js'

const posthogToken = import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com'
const posthogEnabled = import.meta.env.VITE_PUBLIC_POSTHOG_ENABLED !== 'false'

let posthogClient: PostHog | null = null

function initializePostHog() {
  if (!posthogEnabled || !posthogToken) return null
  if (posthogClient) return posthogClient

  posthog.init(posthogToken, {
    api_host: posthogHost,
    defaults: '2026-01-30',
    capture_pageview: false,
    respect_dnt: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      maskCapturedNetworkRequestFn: (request) => {
        if (request.name) request.name = request.name.split('?')[0]
        return request
      },
    },
  })

  posthogClient = posthog
  return posthogClient
}

if (getBrowserAnalyticsConsent() === 'accepted') {
  initializePostHog()
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<PostHog | null>(() => posthogClient)

  useEffect(() => {
    const handleConsentChange = () => {
      if (getBrowserAnalyticsConsent() === 'accepted') {
        setClient(initializePostHog())
      }
    }

    handleConsentChange()
    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleConsentChange)
    return () => window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, handleConsentChange)
  }, [])

  if (!client) return <>{children}</>

  return <PostHogProvider client={client}>{children}</PostHogProvider>
}

export function isAnalyticsConfigured() {
  return posthogEnabled && !!posthogToken
}

export function isAnalyticsReady() {
  return posthogClient !== null
}

export function useAnalytics() {
  return usePostHog()
}
